import { useMemo, useState } from 'react';

import { supabase } from '../../lib/supabase';
import { useAppDispatch } from '../../store/store';
import { authActions } from '../../store/slices/auth';
import { useConsent, type ConsentState } from '../../hooks/useConsent';
import { IDADE_MINIMA, idadeEmAnos } from '../../utils/age';
import type { Ir } from '../porta';

// A MAIORIDADE E O ACEITE DOS DOCUMENTOS (LGPD) — a lógica do antigo `pages/Consent`, sem o desenho.
//
// Uma tela só para os três casos: cadastro por e-mail, login pelo Google (que não passa por
// formulário nenhum) e as contas criadas antes desta exigência. Quem decide se o dado vale é a
// edge function `account-consent`; aqui a validação só dá retorno imediato.
// Mora no núcleo desde 01/11/2026 (docs/PLANO-APP-NATIVO.md): a web e o app usam a mesma regra.

export { IDADE_MINIMA };

export const useConsentimento = ({ ir: navigate }: { ir: Ir }) => {
  const dispatch = useAppDispatch();
  const { state, apply } = useConsent();

  const [birthDate, setBirthDate] = useState('');
  const [maioridade, setMaioridade] = useState(false);
  const [termos, setTermos] = useState(false);
  const [politica, setPolitica] = useState(false);
  const [comunicacoes, setComunicacoes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reaceite de uma versão nova não volta a pedir a data: o servidor reaproveita a já registrada.
  const precisaData = state?.needsBirthDate !== false;
  const docsPendentes = state?.pendingDocs ?? [];
  const soReaceite = docsPendentes.length > 0 && !precisaData;

  const idade = useMemo(() => (birthDate ? idadeEmAnos(birthDate) : null), [birthDate]);
  const dataInvalida = birthDate !== '' && idade === null;
  const menorDeIdade = idade !== null && idade >= 0 && idade < IDADE_MINIMA;
  const dataFutura = idade !== null && idade < 0;

  const podeEnviar =
    !loading &&
    maioridade && termos && politica &&
    (!precisaData || (birthDate !== '' && !dataInvalida && !dataFutura));

  const enviar = async () => {
    setError(null);
    setLoading(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('account-consent', {
        body: {
          action: 'submit',
          ...(precisaData ? { birthDate } : {}),
          aceitaTermos: termos,
          aceitaPolitica: politica,
          aceitaComunicacoes: comunicacoes,
        },
      });
      if (fnError) throw fnError;
      if (data?.error && !data?.blocked) throw new Error(data.error);

      apply(data as ConsentState);
      // O bloqueio de menor tem tela própria.
      navigate(data?.blocked ? '/conta-bloqueada' : '/artists', { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Não foi possível registrar. Tente novamente.');
      setLoading(false);
    }
  };

  const sair = async () => {
    await dispatch(authActions.signOut());
    navigate('/login', { replace: true });
  };

  return {
    precisaData,
    soReaceite,
    birthDate, setBirthDate,
    maioridade, setMaioridade,
    termos, setTermos,
    politica, setPolitica,
    comunicacoes, setComunicacoes,
    dataInvalida,
    dataFutura,
    menorDeIdade,
    podeEnviar,
    loading,
    error,
    enviar,
    sair: () => { void sair(); },
  };
};
